/**
 * RISK.FORMATTER.TS
 * Format risk object
 */
import { formatDate, isToday } from "@/utils/format-date.utils"
import { Risk, RiskScales } from "@/features/risks/entities/risk/model/risk.types"
import {
  //  getStatus,
  getValueInitial,
  getValueResidual,
  updateEvaluation
} from "./risk.utils"
import { Session } from "@/redux/_session.types"
import { Organization } from "@/features/adm/entities/organization/model/organization.types"

/**
 * Fusionne les barèmes du compte et ceux spécifiques au risque (override).
 */
function mergeRiskScales(base: RiskScales, override?: Partial<RiskScales>): RiskScales {
  return {
    severityScale: override?.severityScale ?? base.severityScale,
    frequencyScale: override?.frequencyScale ?? base.frequencyScale,
    preventionScale: override?.preventionScale ?? base.preventionScale,
    residualRiskScale: override?.residualRiskScale ?? base.residualRiskScale
  }
}

/**
 * Formatter function
 */
export function formatRisk(risk: Risk, _session: Session, organization: Organization): Risk {
  // Select appropriate scales based on risk.rps flag
  const severityScale = risk.rps
    ? organization.rpsSeverityScale ?? organization.severityScale
    : organization.severityScale
  const frequencyScale = risk.rps
    ? organization.rpsFrequencyScale ?? organization.frequencyScale
    : organization.frequencyScale
  const preventionScale = risk.rps
    ? organization.rpsPreventionScale ?? organization.preventionScale
    : organization.preventionScale
  const residualRiskScale = risk.rps
    ? organization.rpsResidualRiskScale ?? organization.residualRiskScale
    : organization.residualRiskScale

  const { riskAttributes } = organization

  //Get scales
  const riskScales: RiskScales = mergeRiskScales(
    { severityScale, frequencyScale, preventionScale, residualRiskScale },
    risk.options?.scales
  )

  const {
    severityScale: sevScale,
    frequencyScale: freqScale,
    preventionScale: prevScale,
    residualRiskScale: residualScale
  } = riskScales

  const frequencyScaleMax = Math.max(...freqScale.map((s) => s.value ?? 0))
  const preventionScaleMax = Math.max(...prevScale.map((s) => s.value ?? 0))
  const severityScaleMax = Math.max(...sevScale.map((s) => s.value ?? 0))

  //Get label and value for each scale
  const { label: frequencyLabel, value: frequency } = updateEvaluation(risk.frequency, frequencyScale)
  const { label: preventionLabel, value: prevention } = updateEvaluation(risk.prevention, preventionScale)
  const { label: severityLabel, value: severity } = updateEvaluation(risk.severity, severityScale)

  //Calculate risk level
  const initial = getValueInitial(frequency, severity, residualScale)
  const residual = getValueResidual(initial.score, prevention, residualScale)

  //Get status for the contribution
  // const status = getStatus(risk)

  //Sort attributes
  const orderedAttributes: Record<string, string> = {}
  for (const key of riskAttributes) {
    orderedAttributes[key] = risk.attributes?.[key] ?? ""
  }

  //Get first and second attr
  const name = risk.attributes?.[riskAttributes[0]] ?? ""
  const nameSecondary = risk.attributes?.[riskAttributes[1]] ?? ""

  //Risk that is edited
  // const editedRisk = risk.editedRisk ? formatRisk(risk.editedRisk, _session, organization) : null

  //Date alert = more than ten month yellow, more than one year
  let dateValidated: Date | null = null
  let alertColor: string | null = null
  let alertExpired = false
  if (risk.dateValidated) {
    dateValidated = new Date(risk.dateValidated)
    const now = new Date()
    const diffInMs = now.getTime() - dateValidated.getTime()
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24)
    if (diffInDays > 365) {
      alertExpired = true
      alertColor = "#ff9500"
    } else if (diffInDays > 305) {
      alertColor = "#F2D600"
    }
  }

  return {
    ...risk,

    //Color if alert
    alertColor,
    alertExpired,

    //associations
    associatedRisks: risk.associatedRisks ?? [],

    // default risk = valeur du premier attribut (ex: "Danger")
    name,
    nameSecondary,

    // ajoute l’objet attributes trié
    attributes: orderedAttributes,
    commentary: risk.commentary ? risk.commentary : "",

    // Dates formatées
    dateArchivedLabel: risk.dateArchived ? formatDate(risk.dateArchived, false, false) : "",
    dateValidated,
    dateValidatedLabel: risk.dateValidated ? formatDate(risk.dateValidated, false, false) : "",
    dateValidatedToday: risk.dateValidated ? isToday(risk.dateValidated) : false,

    //Update values and labels
    frequency,
    frequencyLabel,
    frequencyScale: freqScale,

    prevention,
    preventionLabel,
    preventionScale: prevScale,

    residualScale,

    severity,
    severityLabel,
    severityScale: sevScale,

    frequencyScaleMax,
    preventionScaleMax,
    severityScaleMax,

    // Évaluation initiale
    valueInitial: initial.score,
    valueInitialColor: initial.color,
    valueInitialLabel: initial.label,

    // Évaluation résiduelle
    valueEffective: residual.score,
    valueEffectiveColor: residual.color,
    valueEffectiveLabel: residual.label,
    valueEffectiveIndex: residual.index
  }
}
